/**
 * How many times to ask a cloud runtime to accept a connection before giving up.
 *
 * ## Why one budget was not enough
 *
 * The backoff in `cloudRuntimeConnectRetry` was tuned for RESUMING a suspended
 * sprite, and its own docstring records the measurement: "A suspended Sprite
 * resumes on its next inbound request in ~0.5-2s (measured 0.646s live)."
 * Against that, eight attempts over ~34s is generous.
 *
 * The same budget was also being applied to a COLD machine create, which is a
 * different order of magnitude — full sprite bootstrap is documented near 90s.
 * So a brand-new machine was abandoned while still reporting
 * `readinessPhase: "machine_creating"`, and the thread was left showing
 * "Stuck" with nothing wrong except that nobody waited long enough. Observed
 * live: the ladder ran 300ms → 600ms → 1.2s → 2.4s → 4.8s → 9.6s → 15s and
 * stopped, exactly 33.9s, while the machine was still being created.
 *
 * ## Why the default is the LONGER budget
 *
 * Warm is the one case we can positively identify: CMMD says `running` AND
 * reports no readiness phase at all. Everything else gets the cold budget.
 *
 * Note the phase is treated as a signal, not as a claim about its meaning. A
 * value like `supervisor_up` reads as a progress MARKER rather than pending
 * work, so a settled runtime may well carry one and take the long budget. That
 * is accepted deliberately rather than papered over with a hand-maintained list
 * of "phases that mean finished" — such a list is exactly what goes stale when
 * the server grows a case and the client silently does not.
 *
 * That direction matters. Guessing "warm" wrongly reproduces the bug we are
 * fixing: a silent give-up on a machine that was going to be fine. Guessing
 * "cold" wrongly costs only a longer wait on a runtime that will answer on its
 * first attempt anyway, because the budget is a CEILING, not a duration — a
 * runtime that connects immediately never spends it. So an unrecognised phase
 * fails toward patience.
 *
 * ## The ceiling is bounded by the lifecycle abort
 *
 * The cold budget must stay inside `CONNECT_LIFECYCLE_TIMEOUT_MS` (5 minutes),
 * or the retry ladder would outlive the abort that is supposed to contain it
 * and the user would see the connect die mid-ladder for a second, unrelated
 * reason. 18 attempts is ~184s of gaps — roughly twice the documented
 * bootstrap, and comfortably inside five minutes.
 */

/** Enough for a suspended sprite, which answers in about a second. */
export const WARM_CONNECT_MAX_ATTEMPTS = 8;

/** ~184s of gaps: about 2x documented cold bootstrap, inside the 5-minute abort. */
export const COLD_CONNECT_MAX_ATTEMPTS = 18;

export interface RuntimeReadinessSignal {
  readonly status: string | null;
  readonly readinessPhase: string | null;
}

function isBlank(value: string | null): boolean {
  return value === null || value.trim().length === 0;
}

/**
 * Warm ONLY when CMMD positively says so: status `running` and no readiness
 * phase reported at all. Anything else is treated as a cold boot.
 */
export function isWarmRuntime(signal: RuntimeReadinessSignal): boolean {
  return signal.status?.trim() === "running" && isBlank(signal.readinessPhase);
}

export function connectAttemptBudget(signal: RuntimeReadinessSignal): number {
  return isWarmRuntime(signal) ? WARM_CONNECT_MAX_ATTEMPTS : COLD_CONNECT_MAX_ATTEMPTS;
}
